import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { injectFirebaseContextRunner } from '../@firebase/firebase-injection-context';

export interface ClientLogContext {
  correlationId?: string;
  operationId?: string;
  route?: string;
  component?: string;
  errorCode?: string;
}

const SENSITIVE_KEY =
  /(password|passcode|token|secret|api.?key|credential|authorization|cookie|private.?key|client.?secret|invitation|verification.?code|refresh.?token|id.?token|access.?token|card.?number|cvv)/i;
const OMITTED_KEY = /^(body|message|content|text|notes?)$/i;

export function sanitizeClientLogValue(value: unknown, key = '', depth = 0): unknown {
  if (SENSITIVE_KEY.test(key)) return '[REDACTED]';
  if (OMITTED_KEY.test(key)) return '[OMITTED]';
  if (typeof value === 'string') return value.slice(0, 256);
  if (value === null || typeof value !== 'object') return value;
  if (depth >= 4) return '[TRUNCATED]';
  if (Array.isArray(value)) {
    return value.slice(0, 50).map((item) => sanitizeClientLogValue(item, '', depth + 1));
  }
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .slice(0, 50)
      .map(([entryKey, entryValue]) => [
        entryKey,
        sanitizeClientLogValue(entryValue, entryKey, depth + 1),
      ]),
  );
}

function createClientCorrelationId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `client-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
}

@Injectable({
  providedIn: 'root',
})
export class AppLoggerService {
  private readonly analytics = inject(Analytics, { optional: true });
  private readonly runFirebase = injectFirebaseContextRunner();

  private readonly isConsoleEnabled = environment.debug.consolelogs ?? true;

  /**
   * Logs informational messages to the console.
   * @param data
   */
  log(...data: unknown[]): void {
    const safeData = this.safeData(data);
    this.track('log', safeData);
    if (this.isConsoleEnabled) {
      console.log(...safeData);
    }
  }

  /**
   * Logs informational messages to the console (alias for log).
   * @param data
   */
  info(...data: unknown[]): void {
    const safeData = this.safeData(data);
    this.track('info', safeData);
    if (this.isConsoleEnabled) {
      console.info(...safeData);
    }
  }

  /**
   * Logs warning messages to the console.
   * @param data
   */
  warn(...data: unknown[]): void {
    const safeData = this.safeData(data);
    if (this.isConsoleEnabled) {
      console.warn(...safeData);
    }
  }

  /**
   * Logs error messages to the console.
   * @param data
   */
  error(...data: unknown[]): void {
    const safeData = this.safeData(data);
    if (this.isConsoleEnabled) {
      console.error(...safeData);
    }
  }

  /**
   * Logs debug messages to the console.
   * @param data
   */
  debug(...data: unknown[]): void {
    const safeData = this.safeData(data);
    if (this.isConsoleEnabled) {
      console.debug(...safeData);
    }
  }

  /**
   * Logs a table to the console.
   * @param data
   * @param columns Optional list of columns to include in the table.
   */
  table(data: unknown, columns?: string[]): void {
    const safeData = sanitizeClientLogValue(data);
    this.track('table', [safeData, columns]);
    if (this.isConsoleEnabled) {
      if (columns) {
        console.table(safeData, columns);
      } else {
        console.table(safeData);
      }
    }
  }

  /**
   * Starts a timer with the given label.
   * @param label The label for the timer.
   */
  time(label: string): void {
    this.track('time', [label]);
    if (this.isConsoleEnabled) {
      console.time(label);
    }
  }

  /**
   * Ends a timer with the given label and logs the elapsed time.
   * @param label The label for the timer.
   */
  timeEnd(label: string): void {
    this.track('timeEnd', [label]);
    if (this.isConsoleEnabled) {
      console.timeEnd(label);
    }
  }

  /**
   * Logs the current time for the timer with the given label without ending it.
   * @param label The label for the timer.
   * @param data Additional data to log.
   */
  timeLog(label: string, ...data: unknown[]): void {
    const safeData = this.safeData(data);
    this.track('timeLog', [label, ...safeData]);
    if (this.isConsoleEnabled) {
      console.timeLog(label, ...safeData);
    }
  }

  /**
   * Starts a new inline group in the console log.
   * @param label Optional label for the group.
   */
  group(label?: string): void {
    this.track('group', [label]);
    if (this.isConsoleEnabled) {
      if (label) {
        console.group(label);
      } else {
        console.group();
      }
    }
  }

  /**
   * Starts a new collapsed inline group in the console log.
   * @param label Optional label for the group.
   */
  groupCollapsed(label?: string): void {
    this.track('groupCollapsed', [label]);
    if (this.isConsoleEnabled) {
      if (label) {
        console.groupCollapsed(label);
      } else {
        console.groupCollapsed();
      }
    }
  }

  /**
   * Exits the current inline group in the console log.
   */
  groupEnd(): void {
    this.track('groupEnd', []);
    if (this.isConsoleEnabled) {
      console.groupEnd();
    }
  }

  /**
   * Clears the console.
   */
  clear(): void {
    this.track('clear', []);
    if (this.isConsoleEnabled) {
      console.clear();
    }
  }

  /**
   * Logs a stack trace to the console.
   * @param data Additional data to log.
   */
  trace(...data: unknown[]): void {
    const safeData = this.safeData(data);
    if (this.isConsoleEnabled) {
      console.trace(...safeData);
    }
  }

  /**
   * Increments a counter with the given label.
   * @param label The label for the counter.
   */
  count(label?: string): void {
    if (this.isConsoleEnabled) {
      if (label) {
        console.count(label);
      } else {
        console.count();
      }
    }
  }

  /**
   * Resets a counter with the given label.
   * @param label The label for the counter.
   */
  countReset(label?: string): void {
    if (this.isConsoleEnabled) {
      if (label) {
        console.countReset(label);
      } else {
        console.countReset();
      }
    }
  }

  /**
   * Logs an assertion failure if the condition is false.
   * @param condition The condition to assert.
   * @param data Additional data to log if the assertion fails.
   */
  assert(condition: boolean, ...data: unknown[]): void {
    const safeData = this.safeData(data);
    if (this.isConsoleEnabled) {
      console.assert(condition, ...safeData);
    }
  }

  logUnexpectedError(error: unknown, context: ClientLogContext = {}): string {
    const correlationId = context.correlationId ?? createClientCorrelationId();
    const normalizedError =
      error instanceof Error
        ? { name: error.name || 'Error', message: error.message.slice(0, 256) }
        : { name: 'UnknownError', message: 'Unknown client error' };
    this.error('Unexpected application error', {
      ...context,
      correlationId,
      errorName: normalizedError.name,
      errorMessage: normalizedError.message,
    });
    return correlationId;
  }

  private track(eventName: string, data: unknown[]): void {
    if (environment.useFirebaseEmulators) {
      return;
    }

    try {
      if (!this.analytics) {
        return;
      }
      void this.runFirebase(() =>
        logEvent(this.analytics!, eventName, { message: this.format(data) }),
      );
    } catch {
      // Logging must never become the source of an application failure.
    }
  }

  private format(data: unknown[]): string {
    return this.safeData(data)
      .map((value) => {
        if (value instanceof Error) return value.message;
        if (typeof value === 'string') return value;
        try {
          return JSON.stringify(value);
        } catch {
          return '[unserializable value]';
        }
      })
      .join(' ')
      .slice(0, 1000);
  }

  private safeData(data: unknown[]): unknown[] {
    return data.map((value) => sanitizeClientLogValue(value));
  }
}
